import React, { createContext, useContext, useEffect, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './useAuth';

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

export function SocketProvider({ children }: { children: React.ReactNode }) {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const { isAuthenticated } = useAuth();

  const getSocketConfig = () => {
    const fallbackUrl = 'http://localhost:5000';
    const backendUrl = (process.env.REACT_APP_BACKEND_URL || fallbackUrl).trim();

    const normalizeBasePath = (inputPath: string) => {
      const trimmed = inputPath.trim();
      if (!trimmed || trimmed === '/') return '';
      const withLeadingSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
      return withLeadingSlash.replace(/\/$/, '');
    };

    // Absolute backend URL (e.g. https://api.example.com/backend)
    if (/^https?:\/\//i.test(backendUrl)) {
      const parsedUrl = new URL(backendUrl);
      const basePath = normalizeBasePath(parsedUrl.pathname);

      return {
        socketUrl: parsedUrl.origin,
        socketPath: `${basePath}/socket.io`,
      };
    }

    // Relative backend path (e.g. backend or /backend) for same-origin multi-domain deployments
    const basePath = normalizeBasePath(backendUrl);

    return {
      socketUrl: null,
      socketPath: `${basePath}/socket.io`,
    };
  };

  useEffect(() => {
    if (isAuthenticated) {
      // Connect to socket when user is authenticated
      const { socketUrl, socketPath } = getSocketConfig();
      const socketOptions = {
        path: socketPath,
        auth: (cb: (data: object) => void) => cb({ token: localStorage.getItem('token') }),
        transports: ['websocket', 'polling'] as ('websocket' | 'polling')[],
      };

      const newSocket = socketUrl ? io(socketUrl, socketOptions) : io(socketOptions);

      console.log(`Attempting socket connection: ${(socketUrl || window.location.origin)}${socketPath}`);

      newSocket.on('connect', () => {
        console.log('Connected to server...');
        setIsConnected(true);
      });

      newSocket.on('disconnect', () => {
        console.log('Disconnected from server');
        setIsConnected(false);
      });

      setSocket(newSocket);

      return () => {
        newSocket.close();
      };
    } else {
      // Disconnect socket when user is not authenticated
      if (socket) {
        socket.close();
        setSocket(null);
        setIsConnected(false);
      }
    }
  }, [isAuthenticated]);

  const value: SocketContextType = {
    socket,
    isConnected,
  };

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
}

export function useSocket() {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
}

